import { useTranslation } from "react-i18next";
import { SetStateAction, useEffect, useState } from "react";
import { Severity, WeightUnit } from "node-library/dist/constants";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { getPasswordRequirements } from "../../../helpers/passwordHelper";
import { NeutralColor, SemanticColor } from "node-library/dist/colors";
import { CustomTextInput } from "../../../common-components/inputs";
import { ModalWithButtons } from "../../../common-components/modals/modal";
import { BlueButton } from "../../../common-components/button";
import { sendPOSTRequest } from "../../../requestHelper/sendRequest";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { ButtonTextLoadingAnimation } from "../../../common-components/animation/ButtonLoadingAnimation";

interface Props {
	userName: string;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ChangePasswordModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { userName, open, setOpen } = props;
	//for password
	const [currentPassword, setCurrentPassword] = useState<string>("");
	const [password, setPassword] = useState<string>("");
	const [confirmPassword, setConfirmPassword] = useState<string>("");

	//validation
	const [currentPasswordError, setCurrentPasswordError] = useState<string>("");

	const [passwordError, setPasswordError] = useState<string>("");
	const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");

	const passwordRequirements: Array<{
		satisfied: boolean | "";
		description: string;
	}> = getPasswordRequirements(password, translation);

	const [currentErrorColor, setCurrentErrorColor] = useState<string>(
		NeutralColor.NEUTRAL_GREY_300
	);

	const [loading, setLoading] = useState<boolean>(false);

	const fieldsForPassword: Array<any> = [
		{
			id: "current-password-input",
			label: `${translation("CurrentPassword")}`,
			inputType: "password",
			value: currentPassword,
			setValue: setCurrentPassword,
			required: true,
			errorMsg: currentPasswordError,
			setErrorMsg: setCurrentPasswordError,
		},
		{
			id: "new-password-input",
			label: `${translation("NewPassword")}`,
			inputType: "password",
			value: password,
			setValue: setPassword,
			required: true,
			errorMsg: passwordError,
			setErrorMsg: setPasswordError,
			additinoalInfo: passwordRequirements.map((req, index) => {
				return (
					<div
						key={index}
						className="typography-caption"
						style={{
							color: req.satisfied ? SemanticColor.SUCCESS : currentErrorColor,
							marginLeft: "10px",
							display: "flex",
							gap: "5px",
							alignItems: "center",
						}}
					>
						<div style={{ display: "flex", alignItems: "center" }}>
							{req.satisfied ? (
								<CheckIcon sx={{ fontSize: 12 }} />
							) : (
								<CloseIcon sx={{ fontSize: 12 }} />
							)}
						</div>
						<div>{req.description}</div>
					</div>
				);
			}),
		},
		{
			id: "confirm-new-password-input",
			label: `${translation("ConfirmNewPassword")}`,
			inputType: "password",
			value: confirmPassword,
			setValue: setConfirmPassword,
			required: true,
			errorMsg: confirmPasswordError,
			setErrorMsg: setConfirmPasswordError,
		},
	];

	async function handleSubmit() {
		// Simple validation for required fields
		for (const field of fieldsForPassword) {
			if (field.required && !field.value) {
				field.setErrorMsg(translation("MissingInformation"));
				return;
			} else {
				field.setErrorMsg("");
			}
		}
		//password requirement
		for (const req of passwordRequirements) {
			if (!req.satisfied) {
				setCurrentErrorColor(SemanticColor.ERROR);
				return;
			}
		}

		//additional validations
		if (password !== confirmPassword) {
			setConfirmPasswordError(translation("PasswordDoesNotMatch"));
			return;
		}
		try {
			setLoading(true);
			const data = await sendPOSTRequest("/profile/change-password", {
				userName,
				currentPassword,
				newPassword: password,
			});
			openSnackbar(
				translation("PasswordUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			setOpen(false);
		} catch (err: any) {
			if (err.response?.data === "Incorrect Password!") {
				setCurrentPasswordError(translation("Incorrectpassword"));
			} else {
				openSnackbar(
					translation("SystemError"),
					Severity.ERROR,
					translation("SystemErrorMsg"),
					<RedErrorIcon />
				);
			}
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		for (const field of fieldsForPassword) {
			field.setValue("");
			field.setErrorMsg("");
		}
		setCurrentErrorColor(NeutralColor.NEUTRAL_GREY_300);
	}, [open]);

	const fields: Array<React.ReactNode> = fieldsForPassword.map(
		(field, index) => {
			return (
				<CustomTextInput
					id={field.id}
					label={field.label}
					key={field.id}
					value={field.value}
					setValue={field.setValue}
					type="password"
					required={field.required}
					errorMsg={field.errorMsg}
					setErrorMsg={field.setErrorMsg}
					additinoalInfo={field.additinoalInfo}
				/>
			);
		}
	);

	const changePasswordModal: React.ReactNode = (
		<ModalWithButtons
			headerText={""}
			open={open}
			setOpen={setOpen}
			buttons={[
				<BlueButton
					key="change-password-button"
					id="change-password-button"
					size="flex"
					children={
						loading ? (
							<ButtonTextLoadingAnimation
								text={translation("Loading")}
							></ButtonTextLoadingAnimation>
						) : (
							translation("ChangePassword")
						)
					}
					disabled={loading}
					onClick={handleSubmit}
				></BlueButton>,
			]}
		>
			<div className="profile-page-change-password-header">
				<div className="password-lock-icon"></div>
				<div className="typography-heading-03">
					{translation("ChangePassword")}
				</div>
			</div>
			<div className="profile-page-change-password-form">{fields}</div>
		</ModalWithButtons>
	);
	return changePasswordModal;
}
